// src/components/layout/TopNavpanel.jsx
import { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchUnreadCount } from '../../services/notificationsInboxService';
import { Button, Dropdown, Drawer, Space, Divider, Input, Tooltip, Badge } from 'antd';
import { useCart } from '../../context/CartContext';
import { ShoppingCartOutlined } from '@ant-design/icons';
import {
    SearchOutlined, GlobalOutlined, BulbOutlined, BulbFilled,
    UserOutlined, LogoutOutlined, DashboardOutlined,
    GiftOutlined, BellOutlined, InfoCircleOutlined,
    PhoneOutlined, FileTextOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { LANGUAGES } from '../../lib/i18n';
import { ROUTES } from '../../constants/routes';

function TopNavBrand({ onClick }) {
    return (
        <Link to="/" className="tn__brand" onClick={onClick}>
            <span className="tn__brand-name">SOHO</span>
            <span className="tn__brand-suffix">CARD</span>
            <span className="tn__brand-reg">®</span>
        </Link>
    );
}

function MenuDots({ onClick }) {
    return (
        <button type="button" className="tn__menu-dots" aria-label="Open menu" onClick={onClick}>
            <span className="tn__dot tn__dot--tl" />
            <span className="tn__dot tn__dot--tr" />
            <span className="tn__dot tn__dot--bl" />
            <span className="tn__dot tn__dot--br" />
        </button>
    );
}

export default function TopNav({ onOpenSidebar, hideOwnDrawer = false }) {
    const { isDark, locale, setLocale, toggleMode } = useTheme();
    const { isAuthenticated, user, signOut } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const intl = useIntl();
    const { count } = useCart();

    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [unread, setUnread] = useState(0);
    const intervalRef = useRef(null);

    const openMenu = () => {
        if (hideOwnDrawer && typeof onOpenSidebar === 'function') {
            onOpenSidebar();
        } else {
            setMobileOpen(true);
        }
    };

    useEffect(() => {
        if (!user?.id) { setUnread(0); return; }
        const load = () => fetchUnreadCount(user.id).then(setUnread).catch(() => { });
        load();
        intervalRef.current = setInterval(load, 60000);
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
            window.removeEventListener('focus', onFocus);
        };
    }, [user?.id]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 100);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const handleSearch = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            navigate(`${ROUTES.MARKET}?q=${encodeURIComponent(query.trim())}`);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            setMobileOpen(false);
            navigate(ROUTES.LOGIN, { replace: true });
        } catch (e) {
            console.error(e);
        }
    };

    const languageMenu = {
        items: LANGUAGES.map((l) => ({
            key: l.code,
            label: (
                <span className="tn__lang-item">
                    <span className={`tn__lang-name ${locale === l.code ? 'is-active' : ''}`}>{l.nativeLabel}</span>
                    <span className="tn__lang-sub">{l.label}</span>
                    {locale === l.code && <span className="tn__lang-check">✓</span>}
                </span>
            ),
            onClick: () => setLocale(l.code),
        })),
    };

    const currentLang = LANGUAGES.find((l) => l.code === locale) || LANGUAGES[0];

    const userMenu = {
        items: [
            { key: 'dashboard', icon: <DashboardOutlined />, label: <FormattedMessage id="nav.dashboard" />, onClick: () => navigate(ROUTES.DASHBOARD) },
            { key: 'profile', icon: <UserOutlined />, label: 'Profile', onClick: () => navigate(ROUTES.PROFILE) },
            { type: 'divider' },
            { key: 'logout', icon: <LogoutOutlined />, label: <FormattedMessage id="nav.logout" />, danger: true, onClick: handleSignOut },
        ],
    };

    const menuLinks = [
        { to: ROUTES.MARKET, id: 'nav.market', icon: <GiftOutlined /> },
        { to: ROUTES.ABOUT, id: 'footer.about', icon: <InfoCircleOutlined /> },
        { to: ROUTES.CONTACT, id: 'footer.contact', icon: <PhoneOutlined /> },
    ];

    const legalLinks = [
        { to: ROUTES.TERMS, id: 'footer.terms', icon: <FileTextOutlined /> },
        { to: ROUTES.PRIVACY, id: 'footer.privacy', icon: <SafetyCertificateOutlined /> },
    ];

    const go = (path) => { setMobileOpen(false); navigate(path); };

    return (
        <>
            <nav className={`tn ${scrolled ? 'is-scrolled' : ''}`}>
                <div className="tn__left">
                    <MenuDots onClick={openMenu} />
                    <TopNavBrand />
                    <div className="tn__menu">
                        {menuLinks.map((m) => (
                            <Link key={m.to} to={m.to} className={location.pathname === m.to ? 'is-active' : ''}>
                                {intl.formatMessage({ id: m.id })}
                            </Link>
                        ))}
                    </div>
                </div>

                <div className="tn__right">
                    <div className="tn__search">
                        <SearchOutlined />
                        <input
                            type="search"
                            placeholder={intl.formatMessage({ id: 'nav.search' })}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleSearch}
                            aria-label={intl.formatMessage({ id: 'nav.search' })}
                        />
                    </div>

                    <Dropdown menu={languageMenu} trigger={['click']} placement="bottomRight">
                        <button type="button" className="tn__lang" aria-label="Change language">
                            <GlobalOutlined />
                            <span>{currentLang.code.toUpperCase()}</span>
                        </button>
                    </Dropdown>

                    <Button type="text" shape="circle" icon={isDark ? <BulbFilled /> : <BulbOutlined />} onClick={toggleMode} aria-label="Toggle theme" />

                    <Button type="text" shape="circle" className="tn__icon-btn" icon={<BellOutlined />} onClick={() => navigate(ROUTES.NOTIFICATIONS_INBOX)} aria-label="Notifications">
                        {unread > 0 && <span className="tn__badge">{unread > 99 ? '99+' : unread}</span>}
                    </Button>

                    <Tooltip title="Cart">
                        <Button type="text" shape="circle" className="tn__icon-btn" icon={<ShoppingCartOutlined />} onClick={() => navigate(ROUTES.CART)} aria-label="Cart">
                            {count > 0 && <span className="tn__badge">{count > 99 ? '99+' : count}</span>}
                        </Button>
                    </Tooltip>

                    {isAuthenticated ? (
                        <Dropdown menu={userMenu} trigger={['click']} placement="bottomRight">
                            <Button type="primary" shape="round">
                                {user?.user_metadata?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Account'}
                            </Button>
                        </Dropdown>
                    ) : (
                        <Space size={8} className="tn__cta">
                            <Button onClick={() => navigate(ROUTES.LOGIN)}><FormattedMessage id="nav.signIn" /></Button>
                            <Button type="primary" onClick={() => navigate(ROUTES.REGISTER)}><FormattedMessage id="nav.signUp" /></Button>
                        </Space>
                    )}
                </div>
            </nav>

            {!hideOwnDrawer && (
                <Drawer
                    className="tn-drawer"
                    placement={document.documentElement.dir === 'rtl' ? 'right' : 'left'}
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    width={320}
                    closable={false}
                    styles={{ body: { padding: 20 } }}
                    title={null}
                >
                    <div className="tn-drawer__head">
                        <TopNavBrand onClick={() => setMobileOpen(false)} />
                        <Button type="text" onClick={() => setMobileOpen(false)}>✕</Button>
                    </div>

                    {isAuthenticated && (
                        <div className="tn-drawer__account">
                            <div className="tn-drawer__account-avatar"><UserOutlined /></div>
                            <div className="tn-drawer__account-info">
                                <div className="tn-drawer__account-name">
                                    {user?.user_metadata?.name || user?.email?.split('@')[0] || 'Account'}
                                </div>
                                <div className="tn-drawer__account-email">{user?.email}</div>
                            </div>
                        </div>
                    )}

                    <Input
                        className="tn-drawer__search" size="large"
                        prefix={<SearchOutlined />}
                        placeholder={intl.formatMessage({ id: 'nav.search' })}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onPressEnter={() => {
                            if (query.trim()) {
                                navigate(`${ROUTES.MARKET}?q=${encodeURIComponent(query.trim())}`);
                                setMobileOpen(false);
                            }
                        }}
                    />

                    <div className="tn-drawer__quick">
                        <button type="button" className="tn-drawer__quick-btn" onClick={toggleMode}>
                            {isDark ? <BulbFilled /> : <BulbOutlined />}
                            <span>{isDark ? 'Light' : 'Dark'}</span>
                        </button>
                        <button type="button" className="tn-drawer__quick-btn" onClick={() => { setMobileOpen(false); navigate(ROUTES.NOTIFICATIONS_INBOX); }}>
                            <Badge count={unread} size="small" offset={[4, -4]}><BellOutlined /></Badge>
                            <span>Alerts</span>
                        </button>
                        <button type="button" className="tn-drawer__quick-btn" onClick={() => { setMobileOpen(false); navigate(ROUTES.CART); }}>
                            <Badge count={count} size="small" offset={[4, -4]}><ShoppingCartOutlined /></Badge>
                            <span>Cart</span>
                        </button>
                        <Dropdown menu={languageMenu} trigger={['click']} placement="bottomRight">
                            <button type="button" className="tn-drawer__quick-btn">
                                <GlobalOutlined />
                                <span>{currentLang.code.toUpperCase()}</span>
                            </button>
                        </Dropdown>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />

                    <div className="tn-drawer__links">
                        {menuLinks.map((m) => (
                            <Link key={m.to} to={m.to} onClick={() => setMobileOpen(false)} className="tn-drawer__link">
                                {m.icon} {intl.formatMessage({ id: m.id })}
                            </Link>
                        ))}
                        {legalLinks.map((m) => (
                            <Link key={m.to} to={m.to} onClick={() => setMobileOpen(false)} className="tn-drawer__link">
                                {m.icon} <FormattedMessage id={m.id} />
                            </Link>
                        ))}
                    </div>

                    <Divider style={{ margin: '16px 0' }} />

                    {isAuthenticated ? (
                        <Space direction="vertical" className="tn-drawer__actions" style={{ width: '100%' }}>
                            <Button block size="large" icon={<DashboardOutlined />} onClick={() => go(ROUTES.DASHBOARD)}>
                                <FormattedMessage id="nav.dashboard" />
                            </Button>
                            <Button block size="large" icon={<UserOutlined />} onClick={() => go(ROUTES.PROFILE)}>
                                Profile
                            </Button>
                            <Button block size="large" danger icon={<LogoutOutlined />} onClick={handleSignOut}>
                                <FormattedMessage id="nav.logout" />
                            </Button>
                        </Space>
                    ) : (
                        <Space direction="vertical" className="tn-drawer__actions" style={{ width: '100%' }}>
                            <Button block size="large" onClick={() => go(ROUTES.LOGIN)}>
                                <FormattedMessage id="nav.signIn" />
                            </Button>
                            <Button block size="large" type="primary" onClick={() => go(ROUTES.REGISTER)}>
                                <FormattedMessage id="nav.signUp" />
                            </Button>
                        </Space>
                    )}
                </Drawer>
            )}
        </>
    );
}